<template>
  <!--
    Translation order print (چاپ سفارش ترجمه) through the shared print module.
    open(items): fetches each order (translation/{uid}/edit), then prints one
    order per page in the given order. Report key `translation-order`.
  -->
  <div>
    <v-dialog :value="loading" persistent max-width="320" content-class="top-dialog">
      <div class="top-loading" dir="rtl">
        <v-progress-circular indeterminate color="primary" size="22" width="2" />
        <span>{{ $t("translationPrintLoading") }}</span>
      </div>
    </v-dialog>
    <print-studio
      v-if="docs.length"
      v-model="studio"
      report="translation-order"
      :title="$t('translationOrder')"
      :subtitle="subtitle"
      :columns="columns"
      :source="source"
      :built-ins="builtIns"
      :extra-ctx="ctxOf(docs[0])"
      :options="optionDefs"
      :batch="batch"
      :share-text="$t('translationOrder')"
      @error="$emit('error', $event)"
    />
  </div>
</template>

<script>
import PrintStudio from "@/modules/print/PrintStudio.vue";
import { builtInTranslationTemplates } from "@/modules/print/templates";

// Services of an order item: flag key → price × count keys share the prefix.
var SERVICES = [
  "stampTranslator",
  "judicialApproval",
  "foreignApproval",
  "officeServices",
  "verificationServices",
  "equalOriginal",
  "inquiry",
  "embassy",
];

export default {
  name: "TranslationOrderPrint",
  components: { PrintStudio },
  data: () => ({
    studio: false,
    docs: [],
    loading: false,
  }),
  computed: {
    builtIns() {
      return builtInTranslationTemplates;
    },
    subtitle() {
      var d = this.docs[0];
      return (d && d.self && d.self.invoiceSubtitle) || "";
    },
    optionDefs() {
      return [
      ];
    },
    columns() {
      var app = this;
      var has = function (key) {
        return function (r) { return r[key] === "active" ? app.$t("have") : app.$t("notHave"); };
      };
      var name = function (key) {
        return function (r) { return r[key] && r[key].name ? r[key].name : ""; };
      };
      return [
        { key: "documentType", label: this.$t("documentType"), width: 2.2, value: name("documentType") },
        { key: "baseCount", label: this.$t("baseCount"), format: "number", width: 0.9, value: function (r) { return app.num(r.baseCount); } },
        { key: "addDocumentCount", label: this.$t("addDocumentCount"), format: "number", width: 0.9, value: function (r) { return app.num(r.addDocumentCount); } },
        { key: "fromLanguage", label: this.$t("fromLanguage"), width: 1.1, value: name("fromLanguage") },
        { key: "toLanguage", label: this.$t("toLanguage"), width: 1.1, value: name("toLanguage") },
        { key: "stampTranslator", label: this.$t("stampTranslator"), width: 1, value: has("stampTranslator") },
        { key: "judicialApproval", label: this.$t("judicialApproval"), width: 1, value: has("judicialApproval") },
        { key: "foreignApproval", label: this.$t("foreignApproval"), width: 1, value: has("foreignApproval") },
        { key: "inquiry", label: this.$t("inquiry"), width: 1, value: has("inquiry") },
        { key: "basePrice", label: this.$t("translationTariff"), format: "money", width: 1.3, value: function (r) { return app.num(r.basePrice); } },
        { key: "totalCost", label: this.$t("translationCost&approval"), format: "money", width: 1.5, value: function (r) { return app.num(r.totalCost); } },
      ];
    },
    batch() {
      var app = this;
      return this.docs.map(function (d) {
        return { rows: d.article || [], ctx: app.ctxOf(d) };
      });
    },
    source() {
      var app = this;
      var all = function () {
        return [].concat.apply([], app.batch.map(function (b) { return b.rows; }));
      };
      return {
        currentRows: all,
        total: function () { return all().length; },
        fetchPage: function () {
          return Promise.resolve({ rows: all(), lastPage: 1, total: all().length });
        },
      };
    },
  },
  methods: {
    // items: list rows (need `uid`) or already loaded orders (with `article`).
    open(items) {
      var app = this;
      var list = (items || []).filter(Boolean);
      if (!list.length) return;
      app.studio = false;
      app.docs = [];
      app.loading = true;
      Promise.all(
        list.map(function (it) {
          if (it.article) return Promise.resolve(it);
          return app.$axios
            .get(app.$endpoint + "translation/" + (it.uid || it.id) + "/edit", { params: { id: it.uid || it.id } })
            .then(function (res) { return res.data; });
        })
      )
        .then(function (docs) {
          app.docs = docs.filter(Boolean);
          app.$nextTick(function () { app.studio = true; });
        })
        .catch(function (err) { app.$emit("error", err); })
        .finally(function () { app.loading = false; });
    },
    num(v) {
      var n = parseFloat(v);
      return isNaN(n) ? 0 : n;
    },
    totalsOf(items) {
      var app = this;
      var t = { basePriceTotal: 0, addDocumentPriceTotal: 0, specialTranslatTotal: 0, specialTranslatAddPriceTotal: 0, urgentAmount: 0, discount: 0, totalCost: 0 };
      SERVICES.forEach(function (k) { t[k] = 0; });
      (items || []).forEach(function (r) {
        t.basePriceTotal += app.num(r.basePriceTotal);
        t.addDocumentPriceTotal += app.num(r.addDocumentPriceTotal);
        t.specialTranslatTotal += app.num(r.specialTranslatTotal);
        t.specialTranslatAddPriceTotal += app.num(r.specialTranslatAddPriceTotal);
        t.urgentAmount += app.num(r.urgentAmount);
        t.discount += app.num(r.discountTranslate) + app.num(r.discountService);
        t.totalCost += app.num(r.totalCost);
        SERVICES.forEach(function (k) {
          t[k] += app.num(r[k + "Price"]) * app.num(r[k + "Count"]);
        });
      });
      return t;
    },
    person(p, title) {
      p = p || {};
      return {
        title: title,
        lines: [
          { label: this.$t("name"), value: p.business_name || p.name || "" },
          { label: this.$t("phone"), value: p.phone || "" },
          { label: this.$t("mobile"), value: p.mobile || "" },
          { label: this.$t("address"), value: p.address || "" },
        ],
      };
    },
    // Old print page look: approvals row, three cost columns (translation |
    // services | discount, payable, paid, remain), content, translator
    // contact, then the office terms. Built as end HTML so the studio keeps
    // paper / font / header settings. No div/p/table inside a block: the
    // renderer splits end HTML on those tags to break pages.
    endHtmlOf(d, t, payed) {
      var app = this;
      var FA = "۰۱۲۳۴۵۶۷۸۹";
      var fa = function (v) { return String(v).replace(/[0-9]/g, function (c) { return FA[c]; }); };
      var esc = function (v) {
        return String(v == null ? "" : v).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      };
      var money = function (v) { return fa(app.$numberFormat ? app.$numberFormat(v) : v); };
      var any = function (k) { return (d.article || []).some(function (r) { return r[k] === "active"; }); };
      // Height is estimated from text length; hidden filler reserves the real
      // height of a multi-row block so the page break lands correctly.
      var reserve = function (lines) { return '<span style="display:none">' + new Array(lines * 150).join("x") + "</span>"; };
      // tone: soft tinted rows with an accent bar (discount rose, payable blue, remain emerald)
      var TONES = {
        rose: "background:#fff1f2;color:#be123c;border-inline-start:.8mm solid #f43f5e;",
        blue: "background:#eff6ff;color:#1d4ed8;border-inline-start:.8mm solid #3b82f6;",
        green: "background:#ecfdf5;color:#047857;border-inline-start:.8mm solid #10b981;",
      };
      var row = function (label, value, tone) {
        return '<span style="display:flex;justify-content:space-between;gap:3mm;padding:1.2mm 2mm;' +
          (tone ? TONES[tone] + "border-radius:1mm;font-weight:600;" : "") + '"><span>' + esc(label) + ':</span><b style="direction:ltr">' + money(value) + "</b></span>";
      };
      var col = function (rows, extra) {
        return '<span style="display:flex;flex-direction:column;gap:.6mm;flex:1;min-width:0;' + (extra || "") + '">' + rows.join("") + "</span>";
      };
      var box = function (on, label) {
        return '<span style="display:inline-flex;align-items:center;gap:1.5mm;margin-inline-end:6mm;' + (on ? "" : "opacity:.55;") + '">' +
          '<span style="font-size:1.25em">' + (on ? "☑" : "☐") + "</span>" + esc(label) + "</span>";
      };
      var L = function (k) { return app.$t(k); };
      var line = "border-bottom:.25mm solid #d5dbe3;";
      var out = [];
      out.push('<section style="font-size:9.5pt;padding:2mm 0;' + line + 'text-align:start">' +
        '<b style="margin-inline-end:8mm">' + esc(L("typeTranslation&approval")) + "</b>" +
        box(any("unofficial"), L("unofficial")) + box(any("stampTranslator"), L("stampTranslator")) +
        box(any("judicialApproval"), L("judicialApproval")) + box(any("foreignApproval"), L("foreignApproval")) +
        box(any("inquiry"), L("inquiry")) + box(any("embassy"), L("embassy")) + reserve(1) + "</section>");
      out.push('<section style="display:flex;gap:6mm;font-size:9.5pt;padding:2mm 0;' + line + 'text-align:start">' +
        col([
          row(L("baseTranslate"), t.basePriceTotal), row(L("addDocument"), t.addDocumentPriceTotal),
          row(L("specialTranslat"), t.specialTranslatTotal), row(L("specialTranslatAdditional"), t.specialTranslatAddPriceTotal),
          row(L("judicialApprovalPrice"), t.judicialApproval), row(L("foreignApprovalPrice"), t.foreignApproval),
        ]) +
        col([
          row(L("stampTranslatorPrice"), t.stampTranslator), row(L("officeServicesPrice"), t.officeServices),
          row(L("verificationServicesPrice"), t.verificationServices), row(L("equalOriginalPrice"), t.equalOriginal),
          row(L("inquiryPrice"), t.inquiry), row(L("embassyPrice"), t.embassy), row(L("urgentAmount"), t.urgentAmount),
        ]) +
        col([
          row(L("discountTranslate"), t.discount, "rose"), row(L("payable"), t.totalCost, "blue"),
          row(L("payed"), payed), row(L("remain"), t.totalCost - payed, "green"),
        ], "align-self:center") +
        reserve(8) + "</section>");
      if (d.content) out.push('<section style="font-size:9.5pt;text-align:start">' + esc(L("content")) + " : " + esc(d.content) + "</section>");
      var tr = d.translator || {};
      var contact = [
        tr.address ? esc(L("address")) + " : " + esc(tr.address) : "",
        tr.phone ? esc(L("phone")) + " : " + fa(esc(tr.phone)) : "",
        tr.mobile ? esc(L("mobile")) + " : " + fa(esc(tr.mobile)) : "",
      ].filter(Boolean);
      if (contact.length) out.push('<section style="font-size:9.5pt;text-align:start">' + contact.join(" &nbsp;|&nbsp; ") + "</section>");
      return out.join("");
    },
    ctxOf(d) {
      if (!d) return {};
      var self = d.self || {};
      var t = this.totalsOf(d.article);
      var payed = d.payed ? this.num(d.payed.amount) : 0;
      var translator = d.translator || {};
      var ctx = {
        title: this.$t("translationOrder"),
        parties: {
          // the "official" template adds our office box next to the customer
          seller: this.person(self, this.$t("seller")),
          buyer: this.person(d.customer, this.$t("customer")),
        },
        info: [
          { label: this.$t("date"), value: d.date || "" },
          { label: this.$t("deliveryDate"), value: d.delivery_date || "" },
          { label: this.$t("invoic-number"), value: d.number },
          { label: this.$t("registerar"), value: d.user ? d.user.name : "" },
        ],
        itemsLabel: this.$t("translationItems"),
        signatureLabels: [this.$t("translator"), this.$t("customer")],
      };
      var company = translator.business_name || self.abrName;
      if (company) ctx.company = company;
      if (self.logo && self.logo.link) ctx.logo = self.logo.link;
      var end = this.endHtmlOf(d, t, payed);
      if (translator.content) {
        var txt = String(translator.content);
        end += '<section style="font-size:9.5pt;text-align:start;margin-top:3mm"><b>' + this.$t("description") + " :</b></section>" +
          (/<[a-z][\s\S]*>/i.test(txt) ? txt : txt.replace(/\n/g, "<br>"));
      }
      ctx.endHtml = end;
      return ctx;
    },
  },
};
</script>

<style scoped>
.top-loading {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  background: #fff;
  border-radius: 6px;
}
</style>
